import { useState } from "react";
import "../style/testing_practice.css";
import { NavLink } from "react-router-dom";
export default function TestingPractices() {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const accordionData = [
    {
      title: "Test Data Management",
      items: [
        "Use factories or builders to create test data",
        "Keep test data minimal but representative",
        "Use meaningful data that aids debugging",
        "Avoid hardcoding magic numbers or strings",
        "Clean up test data after each test",
      ],
    },
    {
      title: "Continuous Integration",
      items: [
        "Run tests automatically on every commit",
        "Fail the build if tests don't pass",
        "Keep test suites fast (under 10 minutes)",
        "Parallelize test execution when possible",
        "Monitor test coverage trends over time",
      ],
    },
    {
      title: "Test Maintenance",
      items: [
        "Delete obsolete tests promptly",
        "Refactor tests alongside production code",
        "Keep tests DRY using helper functions",
        "Document complex test scenarios",
        "Review test code in pull requests",
      ],
    },
    {
      title: "Code Coverage Goals",
      items: [
        "Aim for 70-80% coverage as a baseline",
        "Critical paths should have 100% coverage",
        "Don't sacrifice test quality for coverage numbers",
        "Focus on branch coverage, not just line coverage",
        "Use coverage reports to find untested code",
      ],
    },
    {
      title: "Testing Async Code",
      items: [
        "Use proper async/await patterns in tests",
        "Set appropriate timeouts for async operations",
        "Test both success and failure scenarios",
        "Mock timers when testing time-dependent code",
        "Avoid race conditions in async tests",
      ],
    },
    {
      title: "Error Message Quality",
      items: [
        "Write clear, descriptive assertion messages",
        "Include context about expected vs actual",
        "Make failures easy to diagnose",
        "Use custom matchers for better errors",
        "Log relevant state when tests fail",
      ],
    },
  ];

  return (
    <div className="practice-guide-wrapper">



      {/* Hero Banner */}
      <section className="hero-guide-banner">
        
          <div className="hero-icon-container">
            <i className="fas fa-lightbulb"></i>
          </div>
          <h1>Testing Best Practices</h1>
          <p>
            Essential guidelines for effective software testing, ensuring
            clarity, maintainability, and quality across your test suites.
          </p>
        
      </section>

      {/* Tabs */}
      {/* NAV TABS */}
      <nav className="nav-tabs">
        <NavLink to="/software-testing">
          <i className="fa-solid fa-book-open"></i> Overview
        </NavLink>

        <NavLink to="/testing-types">
          <i className="fa-solid fa-box"></i> Types
        </NavLink>

        <NavLink to="/testing-pyramid">
          <i className="fa-solid fa-puzzle-piece"></i> Pyramid
        </NavLink>

        <NavLink to="/testing-methods" >
          <i className="fa-solid fa-code"></i> Methods
        </NavLink>

        <NavLink to="/testing-practices" className="active">
          <i className="fa-solid fa-lightbulb"></i> Practices
        </NavLink>

        <NavLink to="/testing-tools">
          <i className="fa-solid fa-screwdriver-wrench"></i> Tools
        </NavLink>
      </nav>

      <main className="guide-content-container">
        <h2 className="section-heading">Testing Best Practices</h2>
        <p className="section-subheading">
          Essential guidelines for effective software testing
        </p>

        {/* DO & DON'T */}
        <div className="practices-panel">
          <div className="practices-column dos-column">
            <h3 className="column-title">
              <i className="fa-solid fa-circle-check"></i> Do's
            </h3>
            <ul>
              <li><strong>Write tests first (TDD)</strong><p>Tests drive better design</p></li>
              <li><strong>Keep tests simple</strong><p>One test, one behavior</p></li>
              <li><strong>Use descriptive names</strong><p>Explain what is tested</p></li>
              <li><strong>Maintain independence</strong><p>No test dependency</p></li>
              <li><strong>Mock dependencies</strong><p>Isolate code</p></li>
              <li><strong>Test edge cases</strong><p>Nulls, limits, extremes</p></li>
              <li><strong>Automate repetitive tests</strong><p>Save time</p></li>
              <li><strong>Refactor tests</strong><p>Tests are code</p></li>
            </ul>
          </div>

          <div className="practices-column donts-column">
            <h3 className="column-title">
              <i className="fa-solid fa-triangle-exclamation"></i> Don'ts
            </h3>
            <ul>
              <li><strong>Don't test internals</strong><p>Test behavior</p></li>
              <li><strong>Don't write flaky tests</strong><p>Be deterministic</p></li>
              <li><strong>Don't skip negatives</strong><p>Test failures</p></li>
              <li><strong>Don't ignore failures</strong><p>Fix immediately</p></li>
              <li><strong>Don't test third-party code</strong><p>Test usage</p></li>
              <li><strong>Don't overcomplicate</strong><p>Keep simple</p></li>
              <li><strong>Don't chase 100%</strong><p>Meaningful coverage</p></li>
              <li><strong>Don't skip assertions</strong><p>Verify outcome</p></li>
            </ul>
          </div>
        </div>

        {/* AAA Pattern */}
        <section className="content-box">
          <h2 className="section-heading">AAA Pattern: Arrange – Act – Assert</h2>

          <pre className="code-block">{`// Arrange
const data = { name: "john", age: 25 };
const validator = new UserValidator();

// Act
const result = validator.validate(data);

// Assert
expect(result.isValid).toBe(true);
expect(result.errors).toHaveLength(0);`}</pre>
        </section>

        {/* Accordion */}
        <h2 className="section-heading">Advanced Testing Practices</h2>

        <div className="accordion-container">
          {accordionData.map((item, index) => (
            <div
              key={index}
              className={`accordion-item ${activeIndex === index ? "active" : ""}`}
            >
              <button
  type="button"
  className="accordion-title"
  onClick={() => toggleAccordion(index)}
>

                {item.title}
                <i className="fa-solid fa-chevron-down"></i>
              </button>

              {activeIndex === index && (
                <div className="accordion-content">
                  <ul>
                    {item.items.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* FIRST */}
        <h3 className="subsection-heading">FIRST Principles</h3>
        <ul className="first-principles-block">
          <li><strong>F – Fast:</strong> Tests run quickly</li>
          <li><strong>I – Independent:</strong> No dependencies</li>
          <li><strong>R – Repeatable:</strong> Same result always</li>
          <li><strong>S – Self-validating:</strong> Clear pass/fail</li>
          <li><strong>T – Timely:</strong> Written before code</li>
        </ul>
      </main>
    </div>
  );
}
